// ============================================================================
// Done today, and Compact / Comfortable (item #650).
//
// Done today: the rows this person opened from Home that have since gone —
// because the thing they asked for was saved through its own screen. Nothing is
// ticked off by hand (TCSD-0366 ruling 3). Kept in the browser per person and per
// Kansas City day, so the strip empties overnight on the same clock the daily
// summaries use (TCSD-0351).
//
// Undo is offered only where the change can be put back through the same door:
// a card sent to Jose goes back to Draft through updateProjectStage, and that
// closes Jose's "ready to invoice" task (notify_src_projects, 2026-10-04). A sub
// change, a rate change or a signed-off step has its own correction path, so no
// Undo is drawn for those. A row taken off the list with Clear or Send to… (item
// #835) lands here straight away with Undo, which puts it back on your list.
//
// Density: a per-person setting, Compact by default (TCSD-0366 ruling 2),
// remembered the way the theme is.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { kcDay, type WorkRow } from './home-work';
import type { RowUndo } from './hand-off';

export interface DoneItem {
  key: string;
  label: string;
  projectId: string | null;
  at: string;
  /** Set for a row that was cleared or sent on: how Undo puts it back. */
  undo?: RowUndo;
}

const safeGet = (k: string): string | null => {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
};
const safeSet = (k: string, v: string) => {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    /* private browsing: the strip lasts for this visit */
  }
};

export function useDoneToday(eid: string | null, rows: WorkRow[], loading: boolean) {
  const day = kcDay();
  const doneKey = eid ? `tcs.doneToday.${eid}.${day}` : null;
  const pendingKey = eid ? `tcs.doneToday.pending.${eid}` : null;
  const [done, setDone] = useState<DoneItem[]>(() => {
    const raw = doneKey ? safeGet(doneKey) : null;
    try {
      return raw ? (JSON.parse(raw) as DoneItem[]) : [];
    } catch {
      return [];
    }
  });
  const persist = useCallback(
    (next: DoneItem[]) => {
      setDone(next);
      if (doneKey) safeSet(doneKey, JSON.stringify(next));
    },
    [doneKey],
  );

  /** Remember the row being opened, so its disappearance can be noticed on return. */
  const opened = useCallback(
    (r: WorkRow) => {
      if (!pendingKey) return;
      const item: DoneItem = {
        key: r.key,
        label: r.ready ? `${r.name} sent to Jose` : `${r.name} — ${r.missing}`,
        projectId: r.itemType === 'project_work_unsent' ? r.entityId : null,
        at: new Date().toISOString(),
      };
      safeSet(pendingKey, JSON.stringify(item));
    },
    [pendingKey],
  );

  const lastRows = useRef<WorkRow[] | null>(null);
  useEffect(() => {
    if (loading || !pendingKey) return;
    lastRows.current = rows;
    const raw = safeGet(pendingKey);
    if (!raw) return;
    let p: DoneItem | null = null;
    try {
      p = JSON.parse(raw) as DoneItem;
    } catch {
      p = null;
    }
    if (!p) return;
    if (!rows.some((r) => r.key === p!.key)) {
      if (!done.some((d) => d.key === p!.key)) {
        const label = p.projectId ? p.label.replace(/ — .*$/, ' sent to Jose') : p.label;
        persist([{ ...p, label }, ...done]);
      }
    }
    // Back on Home either way: what was opened has now been judged.
    safeSet(pendingKey, '');
  }, [rows, loading, pendingKey, done, persist]);

  const forget = useCallback((key: string) => persist(done.filter((d) => d.key !== key)), [done, persist]);

  /** A row that left the list by the person's own hand (Clear or Send to…). */
  const add = useCallback((item: DoneItem) => persist([item, ...done.filter((d) => d.key !== item.key)]), [done, persist]);

  return { done, opened, forget, add };
}

export type Density = 'compact' | 'comfortable';

export function useDensity(eid: string | null): [Density, (d: Density) => void] {
  const key = eid ? `tcs.density.${eid}` : null;
  const [d, setD] = useState<Density>(() => (key && safeGet(key) === 'comfortable' ? 'comfortable' : 'compact'));
  useEffect(() => {
    setD(key && safeGet(key) === 'comfortable' ? 'comfortable' : 'compact');
  }, [key]);
  const set = useCallback(
    (next: Density) => {
      setD(next);
      if (key) safeSet(key, next);
    },
    [key],
  );
  return [d, set];
}
